import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Chip } from '@cbhq/cds-web/chips';
import { SelectOption } from '@cbhq/cds-web/controls';
import { Dropdown } from '@cbhq/cds-web/dropdown';
import { VStack } from '@cbhq/cds-web/layout';
import { Fallback } from '@cbhq/cds-web/layout/Fallback';
import type { PopoverContentPositionConfig } from '@cbhq/cds-web/overlays';
import { TextCaption } from '@cbhq/cds-web/typography/TextCaption';
import { ActionType, ComponentType, logEvent } from '@cbhq/client-analytics';
import { noop } from '@cbhq/identity-shared/utils';

import type { Organization } from '../contexts/OrganizationContext';

type WorkspaceOrganizationPickerProps = {
  /** List of organizations. Undefined means still loading. */
  organizations: Organization[] | undefined;
  /** The currently selected organization. */
  selectedOrganization: Organization | undefined;
  /** Callback when user selects an organization */
  selectOrganization: (organizationId: string) => void;
};

const messages = defineMessages({
  header: {
    id: 'WorkspaceOrganizationPicker.header',
    defaultMessage: 'Your organizations',
    description: 'Header label above the organization list in the picker dropdown',
  },
  entityCount: {
    id: 'WorkspaceOrganizationPicker.entityCount',
    defaultMessage: '{count, plural, one {# entity} other {# entities}}',
    description: 'Number of legal entities in an organization',
  },
});

const contentPosition: PopoverContentPositionConfig = {
  placement: 'bottom-end',
  gap: 1,
};

export function WorkspaceOrganizationPicker({
  organizations,
  selectedOrganization,
  selectOrganization,
}: WorkspaceOrganizationPickerProps) {
  const { formatMessage } = useIntl();
  const handleOrganizationChange = useCallback(
    (organizationId: string) => {
      if (organizationId !== selectedOrganization?.organizationId) {
        logEvent('workspace_organization_switched', {
          componentType: ComponentType.dropdown,
          action: ActionType.select,
        });
      }
      selectOrganization(organizationId);
    },
    [selectOrganization, selectedOrganization?.organizationId],
  );

  if (!selectedOrganization || organizations === undefined || organizations.length === 0) {
    return <Fallback width={200} height={40} shape="circle" />;
  }

  return (
    <Dropdown
      content={
        <VStack overflow="hidden">
          <TextCaption
            as="span"
            color="fgMuted"
            paddingX={3}
            paddingTop={2}
            paddingBottom={1}
            textTransform="uppercase"
          >
            {formatMessage(messages.header)}
          </TextCaption>
          {organizations.map((org) => (
            <SelectOption
              key={org.organizationId}
              value={org.organizationId}
              title={org.organizationName}
              description={
                org.legalEntities.length > 1
                  ? formatMessage(messages.entityCount, { count: org.legalEntities.length })
                  : undefined
              }
            />
          ))}
        </VStack>
      }
      value={selectedOrganization.organizationId}
      testID="WorkspaceOrganizationPicker-Dropdown"
      onChange={handleOrganizationChange}
      enableMobileModal
      contentPosition={contentPosition}
      width={320}
      maxHeight={400}
      showOverlay
    >
      {/* CDS Chip needs onClick for hover/active CSS states; Dropdown owns pointer events */}
      <Chip maxWidth={200} height={40} onClick={noop}>
        {selectedOrganization.organizationName}
      </Chip>
    </Dropdown>
  );
}
